import * as globals from '@wandb/weave/common/css/globals.styles';

import {type RunWithRunsetInfo} from '../../containers/RunsDataLoader';
import {type PlotFontSizeOrAuto} from '../../util/plotHelpers/plotFontSize';
import {type Grouping} from '../../util/queryTypes';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {type GradientStop} from '../GradientPicker';
import {type ReferenceLineConfig} from '../vis/referenceLines';
import {type SemanticLegendPalette} from '../WorkspaceDrawer/Settings/semanticLegend/utils/colorPalettes';
import {type SemanticLegendAssignment} from '../WorkspaceDrawer/Settings/semanticLegend/utils/resolveSemanticLegendAssignment';
import {type PersistedSemanticColorsSettings} from './semanticColors/types';

export type FrontierLineSettingsType = {
  showLabels?: boolean;
  dimPoints?: boolean;
};

export type ScatterPlotConfig = {
  xAxis?: string;
  yAxis?: string;
  zAxis?: string;
  chartTitle?: string;
  showLinearRegression?: boolean;
  showMaxYAxisLine?: boolean;
  maxYAxisLineSettings?: FrontierLineSettingsType;
  showMinYAxisLine?: boolean;
  minYAxisLineSettings?: FrontierLineSettingsType;
  showAvgYAxisLine?: boolean;
  yAxisLineSmoothingWeight?: number;
  color?: string;
  minColor?: string;
  maxColor?: string;
  xAxisLogScale?: boolean;
  yAxisLogScale?: boolean;
  zAxisLogScale?: boolean;
  xAxisMin?: number;
  xAxisMax?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  zAxisMin?: number;
  zAxisMax?: number;
  legendFields?: string[];
  customGradient?: GradientStop[];
  gradientPalette?: SemanticLegendPalette;
  fontSize?: PlotFontSizeOrAuto;
  semanticLegendPalette?: SemanticLegendPalette;
  semanticLegendSettings?: PersistedSemanticColorsSettings;
  referenceLines?: ReferenceLineConfig[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  zAxisLabel?: string;
};

export const SCATTER_BUCKET_SETTING_ROW_CLASS =
  'flex min-h-32 w-full items-center justify-between gap-20';
export const SCATTER_BUCKET_SETTING_LABEL_CLASS = 'ml-14 w-fit shrink-0';

export const DEFAULT_REFERENCE_LINE_COLOR = globals.TEAL_550;

export function getReferenceLineColor(
  line: Pick<ReferenceLineConfig, 'color'>
): string {
  return line.color ?? DEFAULT_REFERENCE_LINE_COLOR;
}

export // default title cases
// "y and z v. x" if z-axis exists
// "y v. x" if no z-axis
// considered "z v. x" if no y-axis, but this doesn't seem to be possible from the UI anymore
function defaultTitle(config: ScatterPlotConfig): string {
  const {xAxis, yAxis, zAxis} = config;
  const xDisplayName = getAxisDisplayName(xAxis);
  const yDisplayName = getAxisDisplayName(yAxis);
  const zDisplayName = getAxisDisplayName(zAxis);
  if (!xDisplayName || !yDisplayName) {
    return '';
  }
  return `${yDisplayName}${
    zDisplayName ? ` and ${zDisplayName}` : ``
  } v. ${xDisplayName}`;
}

export function getAxisDisplayName(axis: string | undefined): string {
  if (!axis) {
    return '';
  }
  return lowercaseSpecialColumnsInTitle(Run.keyStringDisplayName(axis));
}

export function lowercaseSpecialColumnsInTitle(columnName: string): string {
  if (columnName === 'Created') {
    return 'created';
  }
  if (columnName === 'End Time') {
    return 'end time';
  }
  return columnName;
}

export function getTitleFromConfig(config: ScatterPlotConfig): string {
  return config.chartTitle || defaultTitle(config);
}

export type FrontierLineSettingsProps = {
  frontierLineSettings: FrontierLineSettingsType | undefined;
  updateFrontierLineSettings: (
    frontierLineSettings: FrontierLineSettingsType
  ) => void;
};

export type ScatterDataPoint = {
  x: RunTypes.Value;
  y: RunTypes.Value;
  z: RunTypes.Value;
  entityName?: string;
  projectName?: string;
  run?: RunWithRunsetInfo;
  runDisplayName: string;
  uniqueId: string;
  groupKeys: Grouping | undefined; // we need the grouping to get the right color
  color?: string | number | undefined; // The actual color displayed
  semanticBucket?: SemanticLegendAssignment;
  legend?: {
    [key: string]: string;
  };
  highlight?: boolean;
};
